import { apiClient } from '@/lib/api-client';
import { CompleteOnboardingResponse, ContractorOnboardingStatusResponse } from '@/types/api';

// Business info types
export interface BusinessInfoRequest {
    legalName: string;
    dbaName: string;
    ssn: string;
    phoneNumber: string;
    website?: string;
    primaryEmail: string;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
}

export interface BusinessInfoResponse {
    id: string;
    message: string;
    success: boolean;
}

// Classification types
export interface ClassificationRequest {
    federalClassification: string;
}

export interface ClassificationResponse {
    id: string;
    message: string;
    success: boolean;
}

// Contact info types
export interface ContactInfoRequest {
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber: string;
    roleInCompany: string; // This will be the role ID
}

export interface ContactInfoResponse {
    id: string;
    message: string;
    success: boolean;
}

// Get architect/subcontractor data types
export interface Profile {
    id: string;
    organizationId: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    timezone?: string;
    logoUrl?: string;
    createdAt: string;
    updatedAt: string;
}

export interface ArchitectSpecificProfile {
    id: string;
    organizationId: string;
    licenseNumber?: string;
    licenseType?: string;
    taxId?: string;
    ssn?: string;
    dbaName?: string;
    website?: string;
    primaryEmail?: string;
    federalClassification?: string;
    primaryContactFirstName?: string;
    primaryContactLastName?: string;
    primaryContactEmail?: string;
    primaryContactPhone?: string;
    primaryContactRole?: string;
    documents?: any;
    agreementAccepted: boolean;
    digitalSignature?: string;
    signatureDate?: string;
    signatureIpAddress?: string;
    createdAt: string;
    updatedAt: string;
}

export interface SubcontractorSpecificProfile {
    id: string;
    organizationId: string;
    licenseNumber?: string;
    licenseType?: string;
    taxId?: string;
    ssn?: string;
    dbaName?: string;
    website?: string;
    primaryEmail?: string;
    federalClassification?: string;
    primaryContactFirstName?: string;
    primaryContactLastName?: string;
    primaryContactEmail?: string;
    primaryContactPhone?: string;
    primaryContactRole?: string;
    documents?: any;
    agreementAccepted: boolean;
    digitalSignature?: string;
    signatureDate?: string;
    signatureIpAddress?: string;
    createdAt: string;
    updatedAt: string;
}

// User types
export interface UserRole {
    id: string;
    userId: string;
    roleId: string;
}

export interface User {
    id: string;
    organizationId: string;
    fullName: string;
    email: string;
    passwordHash: string;
    isOwner: boolean;
    createdAt: string;
    updatedAt: string;
    verified: boolean;
    status: 'ACTIVE' | 'INACTIVE';
    roles: UserRole[];
}

export interface ArchitectResponse {
    id: string;
    name: string;
    type: string;
    businessEmail: string;
    passwordHash: string;
    createdAt: string;
    updatedAt: string;
    onboardingComplete: boolean;
    profile?: Profile;
    users?: User[];
    architectSpecificProfile?: ArchitectSpecificProfile;
}

export interface SubcontractorResponse {
    id: string;
    name: string;
    type: string;
    businessEmail: string;
    passwordHash: string;
    createdAt: string;
    updatedAt: string;
    onboardingComplete: boolean;
    profile?: Profile;
    users?: User[];
    subcontractorSpecificProfile?: SubcontractorSpecificProfile;
}

// Team members types
export interface TeamMemberRequest {
    fullName: string;
    email: string;
    roleIds: string[];
}

export interface AddTeamMembersRequest {
    teamMembers: TeamMemberRequest[];
}

export interface AddTeamMembersResponse {
    id: string;
    message: string;
    success: boolean;
}

export const contractorsApi = {
    // Get architect data
    getArchitect: async (
        architectId: string
    ): Promise<ArchitectResponse> => {
        return apiClient.get<ArchitectResponse>(`/architects/${architectId}`);
    },

    // Get subcontractor data
    getSubcontractor: async (
        subcontractorId: string
    ): Promise<SubcontractorResponse> => {
        return apiClient.get<SubcontractorResponse>(`/subcontractors/${subcontractorId}`);
    },

    // Update architect business info
    updateArchitectBusinessInfo: async (
        architectId: string,
        data: BusinessInfoRequest
    ): Promise<BusinessInfoResponse> => {
        return apiClient.put<BusinessInfoResponse>(`/architects/${architectId}/business-info`, data);
    },

    // Update subcontractor business info
    updateSubcontractorBusinessInfo: async (
        subcontractorId: string,
        data: BusinessInfoRequest
    ): Promise<BusinessInfoResponse> => {
        return apiClient.put<BusinessInfoResponse>(`/subcontractors/${subcontractorId}/business-info`, data);
    },

    // Update architect classification
    updateArchitectClassification: async (
        architectId: string,
        data: ClassificationRequest
    ): Promise<ClassificationResponse> => {
        return apiClient.put<ClassificationResponse>(`/architects/${architectId}/classification`, data);
    },

    // Update subcontractor classification
    updateSubcontractorClassification: async (
        subcontractorId: string,
        data: ClassificationRequest
    ): Promise<ClassificationResponse> => {
        return apiClient.put<ClassificationResponse>(`/subcontractors/${subcontractorId}/classification`, data);
    },

    // Update architect contact info
    updateArchitectContactInfo: async (
        architectId: string,
        data: ContactInfoRequest
    ): Promise<ContactInfoResponse> => {
        return apiClient.put<ContactInfoResponse>(`/architects/${architectId}/contact-info`, data);
    },

    // Update subcontractor contact info
    updateSubcontractorContactInfo: async (
        subcontractorId: string,
        data: ContactInfoRequest
    ): Promise<ContactInfoResponse> => {
        return apiClient.put<ContactInfoResponse>(`/subcontractors/${subcontractorId}/contact-info`, data);
    },

    // Add architect team members
    addArchitectTeamMembers: async (
        architectId: string,
        data: AddTeamMembersRequest
    ): Promise<AddTeamMembersResponse> => {
        return apiClient.post<AddTeamMembersResponse>(`/architects/${architectId}/team-members`, data);
    },

    // Add subcontractor team members
    addSubcontractorTeamMembers: async (
        subcontractorId: string,
        data: AddTeamMembersRequest
    ): Promise<AddTeamMembersResponse> => {
        return apiClient.post<AddTeamMembersResponse>(`/subcontractors/${subcontractorId}/team-members`, data);
    },

    // Complete architect onboarding
    completeArchitectOnboarding: async (architectId: string): Promise<CompleteOnboardingResponse> => {
        return apiClient.post<CompleteOnboardingResponse>(`/architects/${architectId}/complete-onboarding`, {});
    },

    // Complete subcontractor onboarding
    completeSubcontractorOnboarding: async (subcontractorId: string): Promise<CompleteOnboardingResponse> => {
        return apiClient.post<CompleteOnboardingResponse>(`/subcontractors/${subcontractorId}/complete-onboarding`, {});
    },

    // Get architect onboarding status
    getArchitectOnboardingStatus: async (architectId: string): Promise<ContractorOnboardingStatusResponse> => {
        return apiClient.get<ContractorOnboardingStatusResponse>(`/architects/${architectId}/onboarding-status`);
    },

    // Get subcontractor onboarding status
    getSubcontractorOnboardingStatus: async (subcontractorId: string): Promise<ContractorOnboardingStatusResponse> => {
        return apiClient.get<ContractorOnboardingStatusResponse>(`/subcontractors/${subcontractorId}/onboarding-status`);
    },
};
